import { Orientations, PlaneView } from '../../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { mat3 } from 'gl-matrix';

export class TargetCrosshair {
    protected readonly _planeView: PlaneView | null;
    protected readonly _mode: boolean;
    protected readonly _draw: boolean;

    public constructor(view: PlaneView | null, mode: boolean, draw: boolean) {
        this._planeView = view;
        this._mode = mode;
        this._draw = draw;
        this.setCustomCursor();
    }

    public getOrientationEnum(orientationMatrix: mat3): Orientations {
        const TRANSVERSAL_MATRIX = mat3.fromValues(1, 0, 0, 0, -1, 0, 0, 0, -1);
        const CORONAL_MATRIX = mat3.fromValues(0, -1, 0, 0, 0, 1, -1, 0, 0);
        const SAGITTAL_MATRIX = mat3.fromValues(1, 0, 0, 0, 0, 1, 0, -1, 0);

        if (mat3.equals(orientationMatrix, TRANSVERSAL_MATRIX)) {
            return Orientations.TRANSVERSAL_ORIENTATION;
        } else if (mat3.equals(orientationMatrix, CORONAL_MATRIX)) {
            return Orientations.CORONAL_ORIENTATION;
        } else if (mat3.equals(orientationMatrix, SAGITTAL_MATRIX)) {
            return Orientations.SAGITTAL_ORIENTATION;
        } else {
            throw new Error('Unknown orientation matrix');
        }
    }

    public setCustomCursor() {
        if (this._draw) {
            const orientationMatrix = this._planeView?.getOrientation();
            if (orientationMatrix) {
                const plusCursor = `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2Ij4KICA8bGluZSB4MT0iOCIgeTE9IjAiIHgyPSI4IiB5Mj0iMTYiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIvPgogIDxsaW5lIHgxPSIwIiB5MT0iOCIgeDI9IjE2IiB5Mj0iOCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIi8+Cjwvc3ZnPg==`;
                const minusCursor = `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDE2IDE2Ij48cGF0aCBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iMiIgZD0iTTEwIDE2aDZNOCAwdjE2TTAgOGgxNiIvPjwvc3ZnPg==`;
                const cursorSvg = this._mode ? minusCursor : plusCursor;
                const canvas = this._planeView?.get2DCanvas;
                if (canvas) {
                    const parentElement = canvas.parentElement;
                    if (parentElement) {
                        parentElement.style.cursor = `url(${cursorSvg}) 8 8, crosshair`;
                        parentElement.offsetHeight; // Force reflow to apply cursor
                    } else {
                        console.error('Parent element not found!');
                    }
                } else {
                    document.body.style.cursor = `url(${cursorSvg}) 8 8, crosshair`;
                }
            }
        } else {
            this.resetCursor();
        }
    }

    public resetCursor() {
        const canvas = this._planeView?.get2DCanvas;
        if (canvas) {
            const parentElement = canvas.parentElement;
            if (parentElement) {
                parentElement.style.cursor = 'default';
            }
        } else {
            document.body.style.cursor = 'default';
        }
    }

    public stopAction(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
